import { Editor, getEventTransfer } from 'slate-react';
import {
  MdFormatItalic,
  MdFormatBold,
  MdFormatUnderlined,
  MdCode,
  MdLooksTwo,
  MdLooks3,
  MdFormatQuote,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdInsertLink,
  MdInsertEmoticon,
} from 'react-icons/lib/md';
import { isKeyHotkey } from 'is-hotkey';
import Html from 'slate-html-serializer';
import React from 'react';
import isUrl from 'is-url';

import { EditorToolbar, EditorWrapper, EmojiMenuWrapper } from './components';
import EMOJIS from './emojies';
import convertHtmlRules from './convertHtmlRules';

/**
 * Define the default node type.
 *
 * @type {String}
 */

const DEFAULT_NODE = 'paragraph';

/**
 * No ops.
 *
 * @type {Function}
 */

const noop = e => e.preventDefault();

/**
 * Define hotkey matchers.
 *
 * @type {Function}
 */

const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');
const isCodeHotkey = isKeyHotkey('mod+`');
const html = new Html({ rules: convertHtmlRules });

/**
 * A change helper to standardize wrapping links.
 *
 * @param {Change} change
 * @param {String} href
 */

function wrapLink(change, href) {
  change.wrapInline({
    type: 'link',
    data: { href },
  });

  change.collapseToEnd();
}

/**
 * A change helper to standardize unwrapping links.
 *
 * @param {Change} change
 */

function unwrapLink(change) {
  change.unwrapInline('link');
}

/**
 * The rich text example.
 *
 * @type {Component}
 */

class ArkEdit extends React.Component {
  
  
  constructor(props) {
    super(props);
    const value = props.value ? props.value : '<p></p>';

    this.state = {
      value: html.deserialize(value),
      showEmojis: false,
    };
  }

  /**
   * Check if the current selection has a mark with `type` in it.
   *
   * @param {String} type
   * @return {Boolean}
   */

  hasMark = type => {
    const { value } = this.state;
    return value.activeMarks.some(mark => mark.type === type);
  };

  /**
   * Check if the any of the currently selected blocks are of `type`.
   *
   * @param {String} type
   * @return {Boolean}
   */

  hasBlock = type => {
    const { value } = this.state;
    return value.blocks.some(node => node.type === type);
  };
  /**
   * Check whether the current selection has a link in it.
   *
   * @return {Boolean} hasLinks
   */

  hasLinks = () => {
    const { value } = this.state;
    return value.inlines.some(inline => inline.type === 'link');
  };
  /**
   * When clicking a link, if the selection has a link in it, remove the link.
   * Otherwise, add a new link with an href and text.
   *
   * @param {Event} event
   */

  onClickLink = event => {
    event.preventDefault();
    const { value } = this.state;
    const hasLinks = this.hasLinks();
    const change = value.change();

    if (hasLinks) {
      change.call(unwrapLink);
    } else if (value.isExpanded) {
      const href = window.prompt('Enter the URL of the link:');
      change.call(wrapLink, href);
    } else {
      const href = window.prompt('Enter the URL of the link:');
      const text = window.prompt('Enter the text for the link:');
      change
        .insertText(text)
        .extend(0 - text.length)
        .call(wrapLink, href);
    }

    this.onChange(change);
  };
  /**
   * When clicking a emoji, insert it
   *
   * @param {Event} e
   */

  onClickEmoji = (e, code) => {
    e.preventDefault();
    const { value } = this.state;
    const change = value.change();

    change
      .insertInline({
        type: 'emoji',
        isVoid: true,
        data: { code },
      })
      .collapseToStartOfNextText()
      .focus();

    this.onChange(change);
  };
  /**
   * On change, save the new `value`.
   *
   * @param {Change} change
   */

  onChange = ({ value }) => {
    if (value.document !== this.state.value.document) {
      this.props.onChange(html.serialize(value));
    }
    this.setState({ value });
  };

  /**
   * On paste, if the text is a link, wrap the selection in a link.
   *
   * @param {Event} event
   * @param {Change} change
   */

  onPaste = (event, change) => {
    if (change.value.isCollapsed) {return;}

    const transfer = getEventTransfer(event);
    const { type, text } = transfer;
    if (type !== 'text' && type !== 'html') {return;}
    if (!isUrl(text)) {return;}

    if (this.hasLinks()) {
      change.call(unwrapLink);
    }

    change.call(wrapLink, text);
    return true;
  };

  /**
   * On key down, if it's a formatting command toggle a mark.
   *
   * @param {Event} event
   * @param {Change} change
   * @return {Change}
   */

  onKeyDown = (event, change) => {
    let mark;

    if (isBoldHotkey(event)) {
      mark = 'bold';
    } else if (isItalicHotkey(event)) {
      mark = 'italic';
    } else if (isUnderlinedHotkey(event)) {
      mark = 'underlined';
    } else if (isCodeHotkey(event)) {
      mark = 'code';
    } else {
      return;
    }

    event.preventDefault();
    change.toggleMark(mark);
    return true;
  };

  /**
   * When a mark button is clicked, toggle the current mark.
   *
   * @param {Event} event
   * @param {String} type
   */

  onClickMark = (event, type) => {
    event.preventDefault();
    const { value } = this.state;
    const change = value.change().toggleMark(type);
    this.onChange(change);
  };

  /**
   * When a block button is clicked, toggle the block type.
   *
   * @param {Event} event
   * @param {String} type
   */

  onClickBlock = (event, type) => {
    event.preventDefault();
    const { value } = this.state;
    const change = value.change();
    const { document } = value;

    // Handle everything but list buttons.

    if (type !== 'bulleted-list' && type !== 'numbered-list') {
      const isActive = this.hasBlock(type);
      const isList = this.hasBlock('list-item');

      if (isList) {
        change
          .setBlocks(isActive ? DEFAULT_NODE : type)
          .unwrapBlock('bulleted-list')
          .unwrapBlock('numbered-list');
      } else {
        change.setBlocks(isActive ? DEFAULT_NODE : type);
      }
    } else {
      // Handle the extra wrapping required for list buttons.
      const isList = this.hasBlock('list-item');
      const isType = value.blocks.some(block => {
        return !!document.getClosest(block.key, parent => parent.type === type);
      });

      if (isList && isType) {
        change
          .setBlocks(DEFAULT_NODE)
          .unwrapBlock('bulleted-list')
          .unwrapBlock('numbered-list');
      } else if (isList) {
        change.unwrapBlock(type === 'bulleted-list' ? 'numbered-list' : 'bulleted-list').wrapBlock(type);
      } else {
        change.setBlocks('list-item').wrapBlock(type);
      }
    }
    this.onChange(change);
  };
  /**
   * When clicking a link, if the selection has a link in it, remove the link.
   * Otherwise, add a new link with an href and text.
   *
   * @param {Event} event
   */

  onClickLink = event => {
    event.preventDefault();
    const { value } = this.state;
    const hasLinks = this.hasLinks();
    const change = value.change();

    if (hasLinks) {
      change.call(unwrapLink);
    } else if (value.isExpanded) {
      const href = window.prompt('Enter the URL of the link:');
      change.call(wrapLink, href);
    } else {
      const href = window.prompt('Enter the URL of the link:');
      const text = window.prompt('Enter the text for the link:');
      change
        .insertText(text)
        .extend(0 - text.length)
        .call(wrapLink, href);
    }

    this.onChange(change);
  };

  onClickEmojiShow = event => {
    event.preventDefault();
    const { showEmojis } = this.state;
    this.setState({ showEmojis: !showEmojis });
  };

  /**
   * Render.
   *
   * @return {Element}
   */

  render() {
    return (
      <div>
        {this.renderToolbar()}
        {this.renderEditor()}
      </div>
    );
  }

  /**
   * Render the toolbar.
   *
   * @return {Element}
   */

  renderToolbar = () => {
    const { showEmojis } = this.state;
    const hasLinks = this.hasLinks();
    return (
      <EditorToolbar className="menu toolbar-menu">
        {this.renderMarkButton('bold', <MdFormatBold />)}
        {this.renderMarkButton('italic', <MdFormatItalic />)}
        {this.renderMarkButton('underlined', <MdFormatUnderlined />)}
        {this.renderMarkButton('code', <MdCode />)}
        {this.renderBlockButton('heading-two', <MdLooksTwo />)}
        {this.renderBlockButton('heading-three', <MdLooks3 />)}
        {this.renderBlockButton('block-quote', <MdFormatQuote />)}
        {this.renderBlockButton('numbered-list', <MdFormatListNumbered />)}
        {this.renderBlockButton('bulleted-list', <MdFormatListBulleted />)}
        <span
          className="button"
          data-active={hasLinks}
          onMouseDown={this.onClickLink}
        >
          <span className="material-icons">
            <MdInsertLink />
          </span>
        </span>
        <span
          className="button"
          data-active={showEmojis}
          onMouseDown={this.onClickEmojiShow}
        >
          <span className="material-icons">
            <MdInsertEmoticon />
          </span>
        </span>
        {showEmojis && (
          <EmojiMenuWrapper>
            {EMOJIS.map((emoji, i) => {
              const onMouseDown = e => this.onClickEmojis(e, emoji);
              return (
                // eslint-disable-next-line react/jsx-no-bind
                <span
                  className="button"
                  key={i}
                  onMouseDown={onMouseDown}
                >
                  <span className="material-icons">{emoji}</span>
                </span>
              );
            })}
          </EmojiMenuWrapper>
        )}
      </EditorToolbar>
    );
  };

  /**
   * Render a mark-toggling toolbar button.
   *
   * @param {String} type
   * @param {String} icon
   * @return {Element}
   */

  renderMarkButton = (type, icon) => {
    const isActive = this.hasMark(type);
    const onMouseDown = event => this.onClickMark(event, type);
    return (
      // eslint-disable-next-line react/jsx-no-bind
      <span
        className="button"
        data-active={isActive}
        onMouseDown={onMouseDown}
      >
        <span className="material-icons">{icon}</span>
      </span>
    );
  };
  /**
   * Check whether the current selection has a link in it.
   *
   * @return {Boolean} hasLinks
   */

  hasLinks = () => {
    const { value } = this.state;
    return value.inlines.some(inline => inline.type === 'link');
  };
  /**
   * Render a mark-toggling toolbar button.
   *
   * @param {String} type
   * @param {String} icon
   * @return {Element}
   */

  renderLinkButton = (type, icon) => {
    const isActive = this.hasMark(type);
    const onMouseDown = event => this.onClickMark(event, type);
    return (
      // eslint-disable-next-line react/jsx-no-bind
      <span
        className="button"
        data-active={isActive}
        onMouseDown={onMouseDown}
      >
        <span className="material-icons">{icon}</span>
      </span>
    );
  };

  /**
   * Render a block-toggling toolbar button.
   *
   * @param {String} type
   * @param {String} icon
   * @return {Element}
   */

  renderBlockButton = (type, icon) => {
    const isActive = this.hasBlock(type);
    const onMouseDown = event => this.onClickBlock(event, type);

    return (
      // eslint-disable-next-line react/jsx-no-bind
      <span
        className="button"
        data-active={isActive}
        onMouseDown={onMouseDown}
      >
        <span className="material-icons">{icon}</span>
      </span>
    );
  };

  /**
   * Render the Slate editor.
   *
   * @return {Element}
   */

  renderEditor = () => {
    return (
      <EditorWrapper>
        <Editor
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          onPaste={this.onPaste}
          placeholder="Enter some rich text..."
          renderMark={this.renderMark}
          renderNode={this.renderNode}
          spellCheck
          value={this.state.value}
        />
      </EditorWrapper>
    );
  };

  /**
   * Render a Slate node.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderNode = props => {
    const { attributes, children, node, isSelected } = props;
    switch (node.type) {
      case 'block-quote':
        return <blockquote {...attributes}>{children}</blockquote>;
      case 'code':
        return (
          <pre {...attributes}>
            <code>{children}</code>
          </pre>
        );
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'heading-two':
        return <h2 {...attributes}>{children}</h2>;
      case 'heading-three':
        return <h3 {...attributes}>{children}</h3>;
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      case 'link': {
        const { data } = node;
        const href = data.get('href');
        return (
          <a
            {...attributes}
            href={href}
          >
            {children}
          </a>
        );
      }
      case 'emoji': {
        const { data } = node;
        const code = data.get('code');
        return (
          <span
            className={`emoji ${isSelected ? 'selected' : ''}`}
            {...props.attributes}
            contentEditable={false}
            onDrop={noop}
          >
            {code}
          </span>
        );
      }
      default:
        return <p {...attributes}>{children}</p>;
    }
  };

  /**
   * Render a Slate mark.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderMark = props => {
    const { children, mark } = props;
    switch (mark.type) {
      case 'bold':
        return <strong>{children}</strong>;
      case 'code':
        return <code>{children}</code>;
      case 'italic':
        return <em>{children}</em>;
      case 'underlined':
        return <u>{children}</u>;
      case 'link': {
        const { data } = mark;
        const href = data.get('href');
        return <a href={href}>{children}</a>;
      }
      default:
        return null;
    }
  };
}

/**
 * Export.
 */

export default ArkEdit;



// WEBPACK FOOTER //
// ./src/arkulpa-fec/components/ArkEdit/index.js